import { useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useCreateManualLicenseOrder, useLicenseRecipes } from './hooks/useLicenseOrders';
import { ccaaLabel, modalityLabel } from './ccaa';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (orderId: string) => void;
}

export function LicenseManualOrderDialog({ open, onClose, onCreated }: Props) {
  const { data: recipes } = useLicenseRecipes();
  const createManual = useCreateManualLicenseOrder();
  const [recipeId, setRecipeId] = useState('');
  const [duration, setDuration] = useState('1y');
  const [paymentSource, setPaymentSource] = useState<'bizum' | 'transfer' | 'manual'>('bizum');
  const [paymentReference, setPaymentReference] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dni, setDni] = useState('');
  const [forceDuplicate, setForceDuplicate] = useState(false);
  const [locale, setLocale] = useState<'es' | 'en'>('es');
  const [error, setError] = useState<string | null>(null);

  const usable = useMemo(
    () => (recipes || []).filter((r) => r.status !== 'broken'),
    [recipes]
  );
  const selected = usable.find((r) => r.id === recipeId);
  const durations = selected?.durationOptions || [];

  if (!open) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const data = await createManual.mutateAsync({
        recipeId,
        duration,
        paymentSource,
        paymentReference,
        contactEmail,
        contactPhone: contactPhone || undefined,
        forceDuplicate,
        locale,
        applicant: {
          firstName,
          lastName,
          dni,
          birthDate: '1990-01-01',
          addressLine1: 'Pendiente de datos',
          city: 'Pendiente',
          postalCode: '00000',
          country: 'ES',
        },
      });
      if (!data?.id) throw new Error('Respuesta inválida');
      onCreated(data.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear el pedido');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl border border-border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-semibold">Pedido manual (Bizum / transferencia)</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-muted">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 p-4 text-sm">
          <p className="text-xs text-muted-foreground">
            No se crea PaymentIntent de Stripe. El cobro queda registrado como{' '}
            {paymentSource}.
          </p>
          <label className="block space-y-1">
            <span className="text-muted-foreground">Receta</span>
            <select
              required
              value={recipeId}
              onChange={(e) => {
                setRecipeId(e.target.value);
                const r = usable.find((x) => x.id === e.target.value);
                if (r?.durationOptions[0]) setDuration(r.durationOptions[0].code);
              }}
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            >
              <option value="">Selecciona…</option>
              {usable.map((r) => (
                <option key={r.id} value={r.id}>
                  {ccaaLabel(r.ccaa)} · {modalityLabel(r.modality)} ({r.displayName})
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-muted-foreground">Duración</span>
            <select
              required
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            >
              {durations.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-muted-foreground">Pago</span>
              <select
                value={paymentSource}
                onChange={(e) => setPaymentSource(e.target.value as 'bizum' | 'transfer' | 'manual')}
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
              >
                <option value="bizum">Bizum</option>
                <option value="transfer">Transferencia</option>
                <option value="manual">Manual</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-muted-foreground">Referencia pago</span>
              <input
                required
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-muted-foreground">Nombre</span>
              <input
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-muted-foreground">Apellido</span>
              <input
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
              />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-muted-foreground">DNI / NIE / pasaporte</span>
            <input
              required
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-muted-foreground">Idioma</span>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value === 'en' ? 'en' : 'es')}
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-muted-foreground">Email</span>
            <input
              required
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-muted-foreground">Teléfono</span>
            <input
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={forceDuplicate}
              onChange={(e) => setForceDuplicate(e.target.checked)}
            />
            Forzar aunque haya posibles duplicados
          </label>
          {error ? <p className="text-sm text-rose-500">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createManual.isPending}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50"
            >
              {createManual.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
              Crear pedido
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
